import * as React from "react";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { MainListItems, SecondaryListItems } from "../Dashboard/ListItems";
import logo from "../../Assets/logo.png";
import textLogo from "../../Assets/textLogo.png";
import BASE_URL from "../../utils/baseUrl";
import { Card } from "@mui/material";
import useWindowDimensions from "../../utils/useWindowDimensions";

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

// TODO remove, this demo shouldn't need to reset the theme.
const defaultTheme = createTheme();

const CardEmptyComponent = () => (
  <Typography
    component="h1"
    variant="h6"
    color="#fffff"
    noWrap
    sx={{ flexGrow: 1, fontWeight: "bold", marginTop: "10px" }}
  >
    No data available
    <br />
  </Typography>
);

export default function Analytics() {
  const date7DaysAgo = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  };

  const [open, setOpen] = React.useState(true);
  const [startDate, setStartDate] = React.useState([
    date7DaysAgo(),
    date7DaysAgo(),
  ]);
  const [endDate, setEndDate] = React.useState([
    new Date().toISOString().split("T")[0],
    new Date().toISOString().split("T")[0],
  ]);
  const [analyticsData, setAnalyticsData] = React.useState([]);

  const [currentMonthAndYear, setCurrentMonthAndYear] = React.useState(
    new Date().toLocaleString("default", { month: "long", year: "numeric" })
  );

  const [userAnalyticsData, setUserAnalyticsData] = React.useState({});

  const getAnalyticsData = async (
    startDate,
    endDate,
    eventName,
    firebaseCol
  ) => {
    const response = await fetch(
      `${BASE_URL}/analytics?startDate=${startDate}&endDate=${endDate}&eventName=${eventName}&firebaseCol=${firebaseCol}`
    );
    return response.json();
  };

  const getUserAnalyticsData = async (month, year) => {
    const response = await fetch(
      `${BASE_URL}/analytics/user?month=${month}&year=${year}`
    );
    const responseJson = await response.json();
    setUserAnalyticsData(responseJson);
    console.log("user analytics data", responseJson);
  };

  React.useEffect(() => {
    const responses = [
      getAnalyticsData(
        startDate[0],
        endDate[0],
        "concern_click",
        "concernData"
      ),
      getAnalyticsData(startDate[1], endDate[1], "article_click", "articles"),
    ];
    Promise.all(responses).then((data) => {
      setAnalyticsData(data);
    });
  }, [startDate, endDate]);

  React.useEffect(() => {
    let month =
      new Date(currentMonthAndYear.split(" ")[0] + " 1, 2021").getMonth() + 1;
    if (month < 10) {
      month = "0" + month;
    }
    const year = currentMonthAndYear.split(" ")[1];
    getUserAnalyticsData(month, year);
  }, [currentMonthAndYear]);

  const prepareChartSeries = (data) => {
    const series = [];
    const categories = [];
    data.rows.forEach((row, index) => {
      series.push(parseInt(row.metricValues[0].value));
      categories.push(
        data.firebaseData[index].isEnglishTitle ??
          data.firebaseData[index].englishTitle
      );
    });
    return [series, categories];
  };

  const formatDate = (date) => {
    return date.slice(6, 8) + "/" + date.slice(4, 6) + "/'" + date.slice(2, 4);
  };

  const prepareUserChartSeries = (data) => {
    const series = [];
    const categories = [];
    data.rows.forEach((row, index) => {
      series.push(parseInt(row.metricValues[0].value));
      categories.push(formatDate(row.dimensionValues[0].value));
    });
    return [series, categories];
  };

  const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

  const formatEventName = (eventName) => {
    return [
      capitalize(eventName.split("_").join(" ")),
      `The most clicked ${capitalize(
        eventName.split("_")[0]
      )}s on the application in the past 7 days.`,
    ];
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const { height, width } = useWindowDimensions();

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          style={{ backgroundColor: "#F2A040", boxShadow: "none" }}
          position="absolute"
          open={open}
        >
          <Toolbar
            sx={{
              pr: "24px", // keep right padding when drawer closed
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: "36px",
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h6"
              color="#fffff"
              noWrap
              sx={{ flexGrow: 1, fontWeight: "bold" }}
            >
              Analytics
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: [1],
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                style={{ marginRight: "20px" }}
                height="34px"
                width="48px"
                src={logo}
                alt=""
              />
              <img
                style={{ marginRight: "5px" }}
                height="18px"
                width="110px"
                src={textLogo}
                alt=""
              />
            </Box>
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav">
            {<MainListItems />}
            <Divider sx={{ my: 1 }} />
            {<SecondaryListItems />}
          </List>
        </Drawer>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            width: "100%",
            margin: "120px 40px",
            flexDirection: "row",
            flexWrap: "wrap",
          }}
        >
          {analyticsData.length > 0 &&
            analyticsData.map((data, index) => {
              return (
                <Card
                  style={{
                    width: "98%",
                    padding: "20px",
                    marginBottom: "20px",
                    marginLeft: "1%",
                    marginRight: "1%",
                  }}
                >
                  <Typography
                    component="h1"
                    variant="h6"
                    color="#fffff"
                    noWrap
                    sx={{ flexGrow: 1, fontWeight: "bold" }}
                  >
                    {formatEventName(data.eventName)[0]}
                    <br />
                  </Typography>
                  {formatEventName(data.eventName)[1]}
                  <br />
                  <Box paddingTop={1}>
                    <input
                      style={{ padding: "10px" }}
                      aria-label="Date from"
                      max={endDate[index]}
                      value={startDate[index]}
                      type="date"
                      onChange={(date) => {
                        console.log(date.target.value);
                        setStartDate((prev) => {
                          const newStartDate = [...prev];
                          newStartDate[index] = date.target.value;
                          return newStartDate;
                        });
                      }}
                    />
                    <label style={{ margin: "0px 10px" }}>-</label>
                    <input
                      style={{ padding: "10px" }}
                      aria-label="Date to"
                      max={new Date().toISOString().split("T")[0]}
                      min={startDate[index]}
                      value={endDate[index]}
                      type="date"
                      onChange={(date) => {
                        setEndDate((prev) => {
                          const newEndDate = [...prev];
                          newEndDate[index] = date.target.value;
                          return newEndDate;
                        });
                      }}
                    />
                  </Box>
                  {data.rows.length === 0 ? (
                    <CardEmptyComponent />
                  ) : (
                    <BarChart
                      colors={["#F2A040"]}
                      width={width - 350}
                      height={300}
                      series={[{ data: prepareChartSeries(data)[0] }]}
                      xAxis={[
                        {
                          id: "barCategories",
                          data: prepareChartSeries(data)[1],
                          scaleType: "band",
                        },
                      ]}
                    />
                  )}
                </Card>
              );
            })}
          <Card
            style={{
              width: "98%",
              padding: "20px",
              marginBottom: "20px",
              marginLeft: "1%",
              marginRight: "1%",
            }}
          >
            <Typography
              component="h1"
              variant="h6"
              color="#fffff"
              noWrap
              sx={{ flexGrow: 1, fontWeight: "bold" }}
            >
              Active Users
              <br />
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              alignItems={"center"}
              flexDirection="row"
            >
              <IconButton
                style={{ marginRight: "10px" }}
                onClick={() =>
                  setCurrentMonthAndYear(
                    new Date(
                      new Date(
                        currentMonthAndYear.split(" ")[0] +
                          " " +
                          currentMonthAndYear.split(" ")[1]
                      ).setMonth(
                        new Date(
                          currentMonthAndYear.split(" ")[0] +
                            " " +
                            currentMonthAndYear.split(" ")[1]
                        ).getMonth() - 1
                      )
                    ).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })
                  )
                }
              >
                <ChevronLeftIcon />
              </IconButton>
              {currentMonthAndYear}
              <IconButton
                style={{ marginLeft: "10px" }}
                disabled={
                  new Date().toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  }) === currentMonthAndYear
                }
                onClick={() =>
                  setCurrentMonthAndYear(
                    new Date(
                      new Date(
                        currentMonthAndYear.split(" ")[0] +
                          " " +
                          currentMonthAndYear.split(" ")[1]
                      ).setMonth(
                        new Date(
                          currentMonthAndYear.split(" ")[0] +
                            " " +
                            currentMonthAndYear.split(" ")[1]
                        ).getMonth() + 1
                      )
                    ).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })
                  )
                }
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
            {Object.keys(userAnalyticsData).length > 0 &&
            userAnalyticsData.rows.length > 0 ? (
              <LineChart
                colors={["#F2A040"]}
                width={width - 350}
                height={300}
                series={[
                  { data: prepareUserChartSeries(userAnalyticsData)[0] },
                ]}
                xAxis={[
                  {
                    id: "barCategories",
                    data: prepareUserChartSeries(userAnalyticsData)[1],
                    scaleType: "point",
                  },
                ]}
              />
            ) : (
              <CardEmptyComponent />
            )}
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
